'use client'

import { motion } from 'framer-motion'
import { Check, TrendingUp } from 'lucide-react'

interface AutomotiveScaleProofSectionProps {
  heading: string
  description?: string
  bullets: string[]
}

export function AutomotiveScaleProofSection({ heading, description, bullets }: AutomotiveScaleProofSectionProps) {
  return (
    <section className="relative py-24 overflow-hidden bg-muted/20">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6"
          >
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="eyebrow">
              Proven at Scale
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
          >
            {heading}
          </motion.h2>
          {description && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="reveal reveal-delay-2 text-lg text-muted-foreground"
            >
              {description}
            </motion.p>
          )}
        </div>

        {/* Bullets Grid */}
        <div className="grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
          {bullets.map((item, index) => (
            <motion.div
              key={item}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{
                delay: index * 0.1,
                duration: 0.5,
                ease: "easeOut"
              }}
              className="reveal group relative rounded-2xl overflow-hidden bg-white dark:bg-zinc-900 p-6 transition-all duration-300 lift-hover"
              style={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                transitionDelay: `${(index % 4) * 80}ms`,
              }}
            >
              {/* Gradient Glow on Hover */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-2/3 opacity-0 group-hover:opacity-60 transition-opacity duration-300"
                style={{
                  background: 'radial-gradient(ellipse at bottom, rgba(47, 95, 232, 0.15) -20%, transparent 70%)',
                  filter: 'blur(40px)',
                }}
              />

              {/* Content */}
              <div className="relative z-10 flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#2F5FE8]/10 to-[#2F5FE8]/20 dark:from-[#2F5FE8]/20 dark:to-[#2F5FE8]/30 flex items-center justify-center border border-[#2F5FE8]/20 dark:border-[#2F5FE8]/20 group-hover:scale-110 transition-transform duration-300">
                  <Check className="h-4 w-4 text-[#2F5FE8] dark:text-[#7EA0F5]" />
                </div>
                <p className="text-sm text-foreground leading-relaxed flex-1">
                  {item}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
